import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, UntypedFormControl, NgControl} from '@angular/forms';
import {MatLegacyFormFieldControl as MatFormFieldControl} from '@angular/material/legacy-form-field';
import {Subject} from 'rxjs';
import {FocusMonitor} from '@angular/cdk/a11y';
import {MatLegacyInput as MatInput} from '@angular/material/legacy-input';
import {ErrorStateMatcher} from '@angular/material/core';
import {take} from 'rxjs/operators';
import { Output, EventEmitter } from '@angular/core';

declare const google: {
  maps: {
    places: { Autocomplete: new (arg0: HTMLInputElement) => any; };
    event: { addListener: (arg0: any, arg1: string, arg2: () => void) => void; };
  };
};

@Component({
  selector: 'app-location-autocomplete',
  templateUrl: './location-autocomplete.component.html',
  styleUrls: ['./location-autocomplete.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: LocationAutocompleteComponent,
    }
  ],
})
export class LocationAutocompleteComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor, MatFormFieldControl<string> {
  static nextId = 0;
  @Output() position = new EventEmitter<{ location: any }>();
  @ViewChild(MatInput, {read: ElementRef, static: true})
  input!: ElementRef;

  private _value = '';
  @Input() set value(value: string) {
    this._value = value;
    this.onChange(this._value);
    this.stateChanges.next();
  };

  get value(): string {
    return this._value;
  }

  stateChanges = new Subject<void>();

  @HostBinding()
  id = `custom-form-field-id-${LocationAutocompleteComponent.nextId++}`;

  private _placeholder = '';
  @Input() set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  get placeholder(): string {
    return this._placeholder;
  }

  get empty(): boolean {
    return Boolean(!this.value);
  }

  focused = false;

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @Input() required = false;
  @Input() disabled = false;
  controlType = 'google-location-autocomplete';

  @HostBinding('attr.aria-describedby') describedBy = '';

  get errorState(): boolean {
    return this.errorMatcher.isErrorState(this.ngControl.control as UntypedFormControl, null);
  };

  readonly userAriaDescribedBy!: string;

  constructor(private focusMonitor: FocusMonitor,
              @Optional() @Self() public ngControl: NgControl,
              private errorMatcher: ErrorStateMatcher) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  onContainerClick(): void {
    this.focusMonitor.focusVia(this.input, 'program');
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  ngAfterViewInit(): void {
    this.autoCompleteInit();
  }

  ngOnInit(): void {
    this.focusMonitor.monitor(this.input).subscribe((focused) => {
      this.focused = Boolean(focused);
      this.stateChanges.next();
    });

    this.focusMonitor.monitor(this.input)
      .pipe(
        take(1)
      ).subscribe(() => this.onTouched());
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.input);
    this.stateChanges.complete();
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  writeValue(value: string): void {
    this.value = value;
  }

  onChange: any = (value: string): void => {
  };

  onTouched: any = (): void => {
  };

  autoCompleteInit(): void {
    const input = this.input.nativeElement as HTMLInputElement;
    const autocomplete = new google.maps.places.Autocomplete(input);
    autocomplete.setFields([
      'formatted_address',
      'name',
      'geometry'
    ]);
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      let place = autocomplete.getPlace();
      this.position.emit({ location: {
        lat: place.geometry.location.lat(),
        long: place.geometry.location.lng(),
        name: place.formatted_address
      }});
      this.value = input.value;
      this.onChange(this.value);
      this.stateChanges.next();
    });
  }

  setDisabledState(isDisabled: boolean): void {
   this.disabled = isDisabled
  }
}
